import React from 'react';
import {
  Box,
  Select,
  MenuItem,
  TextField,
  IconButton,
  InputAdornment,
  Checkbox,
  ListItemText,
} from '@mui/material';
import { RemoveCircleOutline as RemoveRowIcon } from '@mui/icons-material';
import { Controller, Control, UseFormSetValue, useWatch } from 'react-hook-form';
import { CLINIC_DIALOG_SX } from '../../constants/popupStyles';
import { NotificationEntity } from '../../services/patientNotificationService';
import { ENTITY_FIELD_META, getFieldMeta } from '../../config/fieldMetadata';
import { getOperatorsForType, OperatorDef } from '../../config/criteriaOperators';

interface CriteriaRowProps {
  index: number;
  prefix: string;
  onRemove: (index: number) => void;
  entity: NotificationEntity;
  control: Control<any>;
  setValue: UseFormSetValue<any>;
  dynamicOptions: Record<string, { id: number | string; name: string }[]>;
  extraFields?: import('../../config/fieldMetadata').FieldMeta[];
}

const DEFAULT_OPERATORS: OperatorDef[] = [
  { value: 'equals', label: 'Equals', needsValue: true },
  { value: 'not_equals', label: 'Not Equals', needsValue: true },
  { value: 'contains', label: 'Contains', needsValue: true },
  { value: 'is_empty', label: 'Is Empty', needsValue: false },
  { value: 'is_not_empty', label: 'Is Not Empty', needsValue: false },
];

const CriteriaRow: React.FC<CriteriaRowProps> = ({
  index,
  prefix,
  onRemove,
  entity,
  control,
  setValue,
  dynamicOptions,
  extraFields = [],
}) => {
  const fieldOptions = [...(ENTITY_FIELD_META[entity] ?? []), ...extraFields];

  const watchedField = useWatch({ control, name: `${prefix}.${index}.field` });
  const watchedOperator = useWatch({ control, name: `${prefix}.${index}.operator` });

  const meta = getFieldMeta(entity, watchedField) ?? extraFields.find(f => f.key === watchedField);
  const operators: OperatorDef[] = meta ? getOperatorsForType(meta.dataType) : DEFAULT_OPERATORS;

  const currentOperatorDef = operators.find((op) => op.value === watchedOperator);
  const needsValue = currentOperatorDef?.needsValue !== false;

  const parseMultiValue = (raw: any): string[] => {
    if (Array.isArray(raw)) return raw;
    if (typeof raw === 'string') {
      try { return JSON.parse(raw); } catch { return raw ? [raw] : []; }
    }
    return [];
  };

  const renderValueInput = () => {
    if (!needsValue || !meta) {
      return <Box sx={{ flex: 1 }} />;
    }

    if (meta.dataType === 'enum') {
      if (currentOperatorDef?.multiSelect) {
        const options = meta.enumOptions ?? [];
        return (
          <Controller
            name={`${prefix}.${index}.value`}
            control={control}
            render={({ field }) => {
              const selected = parseMultiValue(field.value);
              return (
                <Select
                  multiple
                  value={selected}
                  onChange={(e) => field.onChange(JSON.stringify(e.target.value))}
                  size="small"
                  sx={{ ...CLINIC_DIALOG_SX.select, flex: 1 }}
                  renderValue={(vals) => (vals as string[]).map(v => options.find(o => o.value === v)?.label ?? v).join(', ')}
                >
                  {options.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      <Checkbox checked={selected.includes(opt.value)} size="small" />
                      <ListItemText primary={opt.label} />
                    </MenuItem>
                  ))}
                </Select>
              );
            }}
          />
        );
      }
      return (
        <Controller
          name={`${prefix}.${index}.value`}
          control={control}
          render={({ field }) => (
            <Select
              {...field}
              value={field.value ?? ''}
              size="small"
              sx={{ ...CLINIC_DIALOG_SX.select, flex: 1 }}
            >
              {(meta.enumOptions ?? []).map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
          )}
        />
      );
    }

    if (meta.dataType === 'dynamic_enum') {
      const source = meta.dynamicSource ?? '';
      const opts = dynamicOptions[source] ?? [];
      if (currentOperatorDef?.multiSelect) {
        return (
          <Controller
            name={`${prefix}.${index}.value`}
            control={control}
            render={({ field }) => {
              const selected = parseMultiValue(field.value);
              return (
                <Select
                  multiple
                  value={selected}
                  onChange={(e) => field.onChange(JSON.stringify(e.target.value))}
                  size="small"
                  sx={{ ...CLINIC_DIALOG_SX.select, flex: 1 }}
                  renderValue={(vals) => (vals as string[]).map(v => opts.find(o => String(o.id) === v)?.name ?? v).join(', ')}
                >
                  {opts.map((opt) => (
                    <MenuItem key={opt.id} value={String(opt.id)}>
                      <Checkbox checked={selected.includes(String(opt.id))} size="small" />
                      <ListItemText primary={opt.name} />
                    </MenuItem>
                  ))}
                </Select>
              );
            }}
          />
        );
      }
      return (
        <Controller
          name={`${prefix}.${index}.value`}
          control={control}
          render={({ field }) => (
            <Select
              {...field}
              value={field.value ?? ''}
              size="small"
              sx={{ ...CLINIC_DIALOG_SX.select, flex: 1 }}
            >
              {opts.map((opt) => (
                <MenuItem key={opt.id} value={String(opt.id)}>{opt.name}</MenuItem>
              ))}
            </Select>
          )}
        />
      );
    }

    if (meta.dataType === 'number') {
      return (
        <Controller
          name={`${prefix}.${index}.value`}
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              type="number"
              size="small"
              placeholder="Value"
              sx={{ flex: 1 }}
              InputProps={{ style: CLINIC_DIALOG_SX.textField as React.CSSProperties }}
            />
          )}
        />
      );
    }

    if (meta.dataType === 'date') {
      if (watchedOperator === 'in_last_x_days' || watchedOperator === 'in_next_x_days') {
        return (
          <Controller
            name={`${prefix}.${index}.value`}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                type="number"
                size="small"
                placeholder="Days"
                sx={{ flex: 1 }}
                InputProps={{
                  style: CLINIC_DIALOG_SX.textField as React.CSSProperties,
                  endAdornment: <InputAdornment position="end">days</InputAdornment>,
                }}
              />
            )}
          />
        );
      }
      if (watchedOperator === 'in_last_x_hours' || watchedOperator === 'in_next_x_hours') {
        return (
          <Controller
            name={`${prefix}.${index}.value`}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                type="number"
                size="small"
                placeholder="Hours"
                sx={{ flex: 1 }}
                InputProps={{
                  style: CLINIC_DIALOG_SX.textField as React.CSSProperties,
                  endAdornment: <InputAdornment position="end">hours</InputAdornment>,
                  inputProps: { min: 1, step: 1 },
                }}
              />
            )}
          />
        );
      }
      return (
        <Controller
          name={`${prefix}.${index}.value`}
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              type="date"
              size="small"
              sx={{ flex: 1 }}
              InputProps={{ style: CLINIC_DIALOG_SX.textField as React.CSSProperties }}
              InputLabelProps={{ shrink: true }}
            />
          )}
        />
      );
    }

    // text (default)
    return (
      <Controller
        name={`${prefix}.${index}.value`}
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            size="small"
            placeholder="Value"
            sx={{ flex: 1 }}
            InputProps={{ style: CLINIC_DIALOG_SX.textField as React.CSSProperties }}
          />
        )}
      />
    );
  };

  return (
    <Box display="flex" gap={1} alignItems="center" mb={1}>
      {/* Field Selector */}
      <Controller
        name={`${prefix}.${index}.field`}
        control={control}
        render={({ field }) => (
          <Select
            {...field}
            size="small"
            sx={{ ...CLINIC_DIALOG_SX.select, minWidth: 130, flex: 1 }}
            onChange={(e) => {
              const newFieldKey = e.target.value as string;
              const newMeta = getFieldMeta(entity, newFieldKey) ?? extraFields.find(f => f.key === newFieldKey);
              const newOperators = newMeta ? getOperatorsForType(newMeta.dataType) : DEFAULT_OPERATORS;
              setValue(`${prefix}.${index}.operator`, newOperators[0]?.value ?? 'equals');
              setValue(`${prefix}.${index}.value`, '');
              field.onChange(e);
            }}
          >
            {fieldOptions.map((opt) => (
              <MenuItem key={opt.key} value={opt.key}>{opt.label}</MenuItem>
            ))}
          </Select>
        )}
      />

      {/* Operator Selector */}
      <Controller
        name={`${prefix}.${index}.operator`}
        control={control}
        render={({ field }) => (
          <Select {...field} size="small" sx={{ ...CLINIC_DIALOG_SX.select, minWidth: 140 }}>
            {operators.map((op) => (
              <MenuItem key={op.value} value={op.value}>{op.label}</MenuItem>
            ))}
          </Select>
        )}
      />

      {/* Value Input — type-aware */}
      {renderValueInput()}

      <IconButton size="small" onClick={() => onRemove(index)} color="error">
        <RemoveRowIcon />
      </IconButton>
    </Box>
  );
};

export default CriteriaRow;
